import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FUEL_TYPES = ["Diesel", "Petrol", "CNG"];
const VEHICLE_TYPE_OPTIONS = ["2 Wheeler", "Hydra", "Truck", "Tempo", "Forklifter"];
const OWNER_TYPE_OPTIONS = ["Personal", "Company Owned"];

export default function FuelLogForm({ form, setField, vehicles }) {
  const selectedVehicle = vehicles.find((v) => v.name === form.vehicle_no);
  const mappedOwner = selectedVehicle?.category === "Employee" ? "Personal" : "Company Owned";
  const ownerType = form.owner_type || mappedOwner || "Company Owned";

  const qty = parseFloat(form.quantity_litres) || 0;
  const amt = parseFloat(form.amount) || 0;
  const ratePerLitre = qty > 0 ? (amt / qty).toFixed(2) : "0.00";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No. *</Label>
          <Select value={form.vehicle_no || ""} onValueChange={(v) => {
            const veh = vehicles.find((x) => x.name === v);
            setField("vehicle_no", v);
            setField("vehicle_name", veh?.details?.vehicle_type || "");
            setField("vehicle_type", veh?.details?.vehicle_type || form.vehicle_type || "");
            setField("owner_type", veh?.category === "Employee" ? "Personal" : "Company Owned");
            setField("employee_name", veh?.details?.employee_name || "");
          }}>
            <SelectTrigger className="h-11" data-testid="field-fuel-vehicle">
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.name}>
                  {v.name}{v.details?.vehicle_type ? ` — ${v.details.vehicle_type}` : ""}{v.category === "Employee" ? " (Personal)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle Type *</Label>
          <Select value={form.vehicle_type || ""} onValueChange={(v) => setField("vehicle_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-fuel-vehicle-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPE_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Owner Type *</Label>
          <Select value={ownerType} onValueChange={(v) => setField("owner_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-fuel-owner-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OWNER_TYPE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {ownerType === "Personal" && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Employee Name</Label>
            <Input value={form.employee_name || ""} onChange={(e) => setField("employee_name", e.target.value)} className="h-11" data-testid="field-fuel-employee" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Fuel Type *</Label>
          <Select value={form.fuel_type || "Diesel"} onValueChange={(v) => setField("fuel_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-fuel-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-slate-50/50 space-y-3">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">Fuel & Odometer Details</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Quantity (Litres) *</Label>
            <Input type="number" step="0.01" value={form.quantity_litres || ""} onChange={(e) => setField("quantity_litres", e.target.value)} className="h-10" placeholder="0.00" data-testid="field-fuel-qty" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Amount (Rs.) *</Label>
            <Input type="number" step="0.01" value={form.amount || ""} onChange={(e) => setField("amount", e.target.value)} className="h-10" placeholder="0.00" data-testid="field-fuel-amount" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Rate / Litre</Label>
            <div className="h-10 flex items-center px-3 bg-gray-50 border rounded text-sm font-semibold" data-testid="rate-per-litre">
              Rs. {ratePerLitre}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500">Odometer (Km) *</Label>
            <Input type="number" step="0.1" value={form.odometer_reading || ""} onChange={(e) => setField("odometer_reading", e.target.value)} className="h-10" placeholder="e.g. 45280" data-testid="field-fuel-odometer" />
          </div>
        </div>
        <p className="text-[10px] text-gray-400">Mileage (Km/L) is auto-calculated from the previous fuel entry for this vehicle.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Fuel Station / Pump Name</Label>
          <Input value={form.pump_name || ""} onChange={(e) => setField("pump_name", e.target.value)} className="h-11" data-testid="field-pump-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
          <Input value={form.remarks || ""} onChange={(e) => setField("remarks", e.target.value)} className="h-11" />
        </div>
      </div>
    </div>
  );
}
